import React from 'react';
import { Navigate, NavLink, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { Building2, Gift, LayoutDashboard, ListChecks, Radar, Send, ShieldCheck, UserPlus } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { SkeletonPageHeader, Skeleton } from '../../components/common/SkeletonLoader';
import { useAuth } from '../../hooks/useAuth';
import { usePartnerReferrals } from '../../hooks/usePartnerReferrals';
import { useMyReferralProgramAccess, type MyReferralProgramAccess, type PartnerReferralsGateContext } from '../../hooks/useMyReferralProgramAccess';
import { MY_PROGRAM_PATH } from '../../utils/partnerReferrals';
import type { PartnerPortalContext } from './portalContext';

/**
 * Partner Referrals inside VALUTTEN, two sides under one side-nav item:
 *
 *   - the referrer side: a subscriber's broker (or an owner/admin/assistant on
 *     a broker's behalf) refers clients to a referral partner and follows them
 *     to settlement (docs/plans/2026-09-26-PARTNER-NETWORK-PORTAL.md);
 *   - "My referral program": the desk of a referral program this company
 *     operates itself (docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §16),
 *     for super_admin, administrator and marketing. marketing sees only this.
 *
 * Tabs are data: one entry per tab, each with a nested route in AppRoutes.
 */
type Tab = { to: string; label: string; icon: React.ComponentType<{ className?: string }>; end?: boolean; fees?: boolean };

const REFERRER_TABS: Tab[] = [
  { to: '/partner-referrals', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/partner-referrals/refer', label: 'Refer a client', icon: UserPlus },
  { to: '/partner-referrals/clients', label: 'My clients', icon: ListChecks },
  { to: '/partner-referrals/rewards', label: 'Rewards', icon: Gift, fees: true },
  { to: '/partner-referrals/radar', label: 'Back book radar', icon: Radar },
  { to: '/partner-referrals/compliance', label: 'Compliance', icon: ShieldCheck },
];

export const MY_PROGRAM_TAB: Tab = { to: MY_PROGRAM_PATH, label: 'My referral program', icon: Building2 };

/** The tabs a viewer sees, in order. Pure, so the gating is unit-tested. */
export function partnerReferralTabs(opts: { referrerSide: boolean; hasProgram: boolean; hideCommissions: boolean }): Tab[] {
  const tabs = opts.referrerSide ? REFERRER_TABS.filter((t) => !(t.fees && opts.hideCommissions)) : [];
  return opts.hasProgram ? [...tabs, MY_PROGRAM_TAB] : tabs;
}

const TabNav: React.FC<{ tabs: Tab[] }> = ({ tabs }) => (
  <nav aria-label="Partner referrals" className="border-b border-gray-700">
    <div className="flex gap-1 overflow-x-auto pb-2">
      {tabs.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          className={({ isActive }) =>
            `flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              isActive
                ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30'
                : 'text-gray-400 hover:text-white hover:bg-gray-800/60 border border-transparent'
            }`
          }
        >
          <Icon className="w-4 h-4" />
          <span>{label}</span>
        </NavLink>
      ))}
    </div>
  </nav>
);

const SUBTITLE = 'Refer a client to a trusted partner and follow them through to settlement';
const DESK_SUBTITLE = 'Work the referrals that come into your own referral program';

/** Referrer side (and "My referral program" for admins whose company runs a program). */
const ReferrerSideLayout: React.FC<{ parentCtx: Record<string, unknown> | null; my: MyReferralProgramAccess }> = ({ parentCtx, my }) => {
  const { hideCommissions } = useAuth();
  const portal = usePartnerReferrals();
  const context: PartnerPortalContext = { ...(parentCtx ?? {}), ...portal, hideFees: hideCommissions, myProgram: my };
  const tabs = partnerReferralTabs({ referrerSide: true, hasProgram: my.hasProgram, hideCommissions });

  return (
    <div className="space-y-6">
      <PageHeader icon={Send} title="Partner Referrals" subtitle={SUBTITLE} onRefresh={portal.refresh} isRefreshing={portal.refreshing} />
      <TabNav tabs={tabs} />
      <Outlet context={context} />
    </div>
  );
};

/** Whether a path is the program desk (program/ or below), where a desk-only viewer lands. */
export function isMyProgramPath(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, '');
  return p === MY_PROGRAM_PATH || p.startsWith(`${MY_PROGRAM_PATH}/`);
}

/**
 * Desk-only viewers (marketing, admitted only because the company runs a
 * referral program; and every desk role in a company whose refer-out switch is
 * off, featureFlags.partner_network_refer_out false; no referrer-side
 * access): just the program desk. They
 * land on "My referral program" from /partner-referrals or any referrer tab
 * (Overview, Refer, My clients, Rewards, Radar, Compliance), whose callables
 * would refuse them. Viewers with both kinds of access use ReferrerSideLayout
 * and keep the Overview landing.
 */
const DeskOnlyLayout: React.FC<{ parentCtx: Record<string, unknown> | null; my: MyReferralProgramAccess }> = ({ parentCtx, my }) => {
  const { pathname } = useLocation();
  if (!my.hasProgram) {
    if (my.desk.status === 'idle' || my.desk.status === 'loading') return <PartnerReferralsSkeleton />;
    return <Navigate to="/" replace />;
  }
  if (!isMyProgramPath(pathname)) return <Navigate to={MY_PROGRAM_PATH} replace />;
  return (
    <div className="space-y-6">
      <PageHeader icon={Send} title="Partner Referrals" subtitle={DESK_SUBTITLE} onRefresh={my.desk.refresh} />
      <TabNav tabs={partnerReferralTabs({ referrerSide: false, hasProgram: true, hideCommissions: my.hideCommissions })} />
      <Outlet context={{ ...(parentCtx ?? {}), myProgram: my }} />
    </div>
  );
};

export const PartnerReferralsSkeleton: React.FC = () => (
  <div className="space-y-6" aria-busy="true">
    <SkeletonPageHeader showSubtitle />
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-64 w-full" />
  </div>
);

const PartnerReferralsPage: React.FC = () => {
  const parentCtx = useOutletContext<Record<string, unknown> | null>();
  const my = useMyReferralProgramAccess(parentCtx as PartnerReferralsGateContext | null);
  if (my.addOnLoading) return <PartnerReferralsSkeleton />;
  return my.referrerSide ? <ReferrerSideLayout parentCtx={parentCtx} my={my} /> : <DeskOnlyLayout parentCtx={parentCtx} my={my} />;
};

export default PartnerReferralsPage;
